import { useMemo } from 'react';
import { admin, palette, radii, shadow, typography } from '../../../../styles/tokens';
import CapabilityTransparencyHint from './CapabilityTransparencyHint';
import { defaultDecisionFor } from './dedupe';
import type { DedupeDecision, DuplicateMatch, NormalizedRow } from './types';

interface DedupePreviewTableProps {
  matches: DuplicateMatch[];
  cleanRows: NormalizedRow[];
  decisions: Record<string, DedupeDecision>;
  onDecisionsChange: (next: Record<string, DedupeDecision>) => void;
  onImport: () => void;
  importing: boolean;
}

const STRENGTH_LABEL: Record<'strong' | 'medium' | 'weak', string> = {
  strong: 'Strong match',
  medium: 'Possible match',
  weak: 'Weak match',
};

const STRENGTH_BG: Record<'strong' | 'medium' | 'weak', string> = {
  strong: palette.amberWash,
  medium: palette.blueWash,
  weak: palette.g100,
};

const STRENGTH_FG: Record<'strong' | 'medium' | 'weak', string> = {
  strong: palette.amber,
  medium: palette.blue,
  weak: palette.g600,
};

export default function DedupePreviewTable({
  matches,
  cleanRows,
  decisions,
  onDecisionsChange,
  onImport,
  importing,
}: DedupePreviewTableProps) {
  const rowsWithoutContact = useMemo(
    () => cleanRows.filter(r => !r.phone && !r.email).length,
    [cleanRows],
  );

  const matchesNotSkipped = useMemo(
    () =>
      matches.filter(
        m =>
          (decisions[m.incoming.rowKey] ??
            defaultDecisionFor(m.strength)) !== 'skip',
      ),
    [matches, decisions],
  );

  const importCount = cleanRows.length + matchesNotSkipped.length;

  const setDecision = (rowKey: string, d: DedupeDecision) => {
    onDecisionsChange({ ...decisions, [rowKey]: d });
  };

  const applyToAll = (d: DedupeDecision) => {
    const next: Record<string, DedupeDecision> = { ...decisions };
    for (const m of matches) next[m.incoming.rowKey] = d;
    onDecisionsChange(next);
  };

  const applyDefaults = () => {
    const next: Record<string, DedupeDecision> = { ...decisions };
    for (const m of matches) next[m.incoming.rowKey] = defaultDecisionFor(m.strength);
    onDecisionsChange(next);
  };

  const mergeAllStrong = () => {
    const next: Record<string, DedupeDecision> = { ...decisions };
    for (const m of matches) {
      if (m.strength === 'strong') next[m.incoming.rowKey] = 'merge';
    }
    onDecisionsChange(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {matches.length > 0 && (
        <section>
          <SectionHeader
            title="Decisions to make"
            subtitle={`${matches.length} possible match${matches.length === 1 ? '' : 'es'} found — choose what to do with each`}
          />

          <div
            style={{
              marginBottom: 12,
              fontSize: typography.size.caption,
              color: admin.caption,
              lineHeight: typography.lineHeight.relaxed,
            }}
          >
            Merging fills in missing fields on the existing client — it never
            overwrites what&rsquo;s already there.
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 12,
            }}
          >
            <BulkButton onClick={applyDefaults}>Apply default to all</BulkButton>
            <BulkButton onClick={() => applyToAll('skip')}>Skip all</BulkButton>
            <BulkButton onClick={mergeAllStrong}>Merge all strong matches</BulkButton>
          </div>

          <div
            style={{
              border: `1px solid ${palette.g150}`,
              borderRadius: radii.md,
              background: palette.white,
              boxShadow: shadow.xs,
              overflow: 'hidden',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: typography.size.caption,
              }}
            >
              <thead>
                <tr style={{ background: palette.g50 }}>
                  <Th>Existing client</Th>
                  <Th>Incoming row</Th>
                  <Th>Match</Th>
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {matches.map(m => {
                  const current =
                    decisions[m.incoming.rowKey] ?? defaultDecisionFor(m.strength);
                  return (
                    <tr
                      key={m.incoming.rowKey}
                      style={{ borderTop: `1px solid ${palette.g150}` }}
                    >
                      <Td>
                        <ClientCell
                          name={m.existingClientName}
                          phone={m.existingClientPhone}
                          email={m.existingClientEmail}
                        />
                      </Td>
                      <Td>
                        <ClientCell
                          name={m.incoming.name}
                          phone={m.incoming.phone}
                          email={m.incoming.email}
                        />
                      </Td>
                      <Td>
                        <StrengthBadge strength={m.strength} />
                      </Td>
                      <Td>
                        <select
                          value={current}
                          onChange={e =>
                            setDecision(
                              m.incoming.rowKey,
                              e.target.value as DedupeDecision,
                            )
                          }
                          style={{
                            padding: '6px 8px',
                            borderRadius: radii.xs,
                            border: `1px solid ${palette.g200}`,
                            background: palette.white,
                            fontSize: typography.size.caption,
                            fontFamily: 'inherit',
                            color: admin.body,
                            cursor: 'pointer',
                          }}
                        >
                          <option value="skip">Skip</option>
                          <option value="import_new">Import as new</option>
                          <option value="merge">Merge into existing</option>
                        </select>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {cleanRows.length > 0 && (
        <section>
          <SectionHeader
            title="Ready to import"
            subtitle={`${cleanRows.length} client${cleanRows.length === 1 ? '' : 's'} ready to import`}
          />
          <div
            style={{
              border: `1px solid ${palette.g150}`,
              borderRadius: radii.md,
              background: palette.white,
              boxShadow: shadow.xs,
              overflow: 'hidden',
              marginBottom: 12,
              maxHeight: 320,
              overflowY: 'auto',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: typography.size.caption,
              }}
            >
              <thead>
                <tr style={{ background: palette.g50 }}>
                  <Th>Name</Th>
                  <Th>Phone</Th>
                  <Th>Email</Th>
                </tr>
              </thead>
              <tbody>
                {cleanRows.map(r => (
                  <tr
                    key={r.rowKey}
                    style={{ borderTop: `1px solid ${palette.g150}` }}
                  >
                    <Td>{r.name || <Muted>—</Muted>}</Td>
                    <Td>{r.phone || <Muted>—</Muted>}</Td>
                    <Td>{r.email || <Muted>—</Muted>}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <CapabilityTransparencyHint rowsWithoutContact={rowsWithoutContact} />
        </section>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onImport}
          disabled={importing || importCount === 0}
          style={{
            padding: '10px 20px',
            background: importing || importCount === 0 ? palette.g300 : palette.accent,
            color: palette.white,
            border: 'none',
            borderRadius: radii.sm,
            fontSize: typography.size.body,
            fontWeight: typography.weight.semibold,
            fontFamily: 'inherit',
            cursor: importing || importCount === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {importing
            ? 'Importing…'
            : `Import ${importCount} client${importCount === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          fontSize: typography.size.heading,
          fontWeight: typography.weight.semibold,
          color: admin.header,
          marginBottom: 2,
        }}
      >
        {title}
      </div>
      <div style={{ fontSize: typography.size.caption, color: admin.caption }}>
        {subtitle}
      </div>
    </div>
  );
}

function BulkButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 12px',
        background: palette.white,
        border: `1px solid ${palette.g200}`,
        borderRadius: radii.xs,
        fontSize: typography.size.caption,
        color: admin.body,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '10px 12px',
        fontSize: typography.size.caption,
        fontWeight: typography.weight.semibold,
        color: admin.caption,
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td
      style={{
        padding: '10px 12px',
        verticalAlign: 'top',
        color: admin.body,
      }}
    >
      {children}
    </td>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span style={{ color: palette.g400 }}>{children}</span>;
}

function ClientCell({
  name,
  phone,
  email,
}: {
  name: string;
  phone: string;
  email: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ fontWeight: typography.weight.medium, color: admin.header }}>
        {name || <Muted>—</Muted>}
      </div>
      <div style={{ color: admin.caption, fontSize: typography.size.micro }}>
        {phone || <Muted>no phone</Muted>}
      </div>
      <div style={{ color: admin.caption, fontSize: typography.size.micro }}>
        {email || <Muted>no email</Muted>}
      </div>
    </div>
  );
}

function StrengthBadge({ strength }: { strength: 'strong' | 'medium' | 'weak' }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: radii.full,
        background: STRENGTH_BG[strength],
        color: STRENGTH_FG[strength],
        fontSize: typography.size.micro,
        fontWeight: typography.weight.semibold,
        whiteSpace: 'nowrap',
      }}
    >
      {STRENGTH_LABEL[strength]}
    </span>
  );
}
